import { EllipsisVerticalIcon } from "@heroicons/react/16/solid";
import { get } from "lodash-es";
import { PlusCircle, Video } from "lucide-react";
import { FC, useCallback, useContext, useRef, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import YouTube from "react-youtube";

import {
  Chip,
  MenuItem,
  MenuList,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MuiLink from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import { useTheme } from "@mui/material/styles";
import {
  GridCellParams,
  GridColDef,
  GridFilterModel,
  GridListColDef,
  GridRenderCellParams,
  GridSortModel,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro";
import { GridRowSelectionModel } from "@mui/x-data-grid/models/gridRowSelectionModel";

import ActionToolbar from "../../components/ActionToolbar";
import DataGridPro from "../../components/DataGridPro";
import DataGridProToolbar from "../../components/DataGridProToolbar";
import DeleteActionDialog, { Entry } from "../../components/DeleteActionDialog";
import FeatureHint from "../../components/FeatureHint";
import Header from "../../components/Header";
import { LoadingError } from "../../components/LoadingError";
import Page from "../../components/Page";
import Quota from "../../components/Quota";
import Link from "../../components/styled/Link";
import SvgIcon from "../../components/styled/SvgIcon";
import useCurrentClusterSupportsSsh from "../../hooks/use-current-cluster-supports-ssh";
import useNavigateWithSearch from "../../hooks/use-navigate-with-search";
import usePaginationModel from "../../hooks/use-pagination-model";
import { usePopover } from "../../hooks/use-popover";
import { useSavedColumnWidths } from "../../hooks/use-saved-column-widths";
import { cn } from "../../lib/utils";
import { useSelector } from "../../store";
import {
  getFilter,
  getOrderBy,
  useListRoutesQuery,
} from "../../store/enhancedApi";
import {
  Route,
  useDeleteRouteMutation,
  useGetOrganizationQuery,
} from "../../store/pomeriumApi";
import { updatedAt } from "../../utils/data-grid-col-defs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserContext } from "@/contexts/UserContext";
import sendStandardEvent from "@/hooks/use-firebase-event";
import { useIsConstrainedCluster } from "@/hooks/use-is-constrained-cluster";
import useQuota from "@/hooks/use-quota";

const defaultColumns = JSON.stringify({
  id: false,
  originatorId: false,
  to: false,
  path: false,
  regex: false,
  prefix: false,
});

const RouteList: FC = () => {
  const { user } = useContext(UserContext);
  const actionsPopover = usePopover();
  const location = useLocation();
  const showBanner = get(location.state, "sampleBanner", false);
  const theme = useTheme();
  const isListView = useMediaQuery(theme.breakpoints.down("sm"));
  const anchorRef = useRef<HTMLElement | null>(null);
  const navigate = useNavigateWithSearch();
  const [selected, setSelected] = useState<GridRowSelectionModel>([]);
  const [routeId, setRouteId] = useState<string>("");
  const [showDelete, setShowDelete] = useState(false);
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const { data: organization } = useGetOrganizationQuery(organizationId);
  const { count: routeCount, limit: routeLimit } = useQuota("routes");
  const supportsSsh = useCurrentClusterSupportsSsh();
  const isConstrainedHostedCluster = useIsConstrainedCluster();

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<string>(
    localStorage.getItem("routeColumns") || defaultColumns,
  );
  const { mutateColumnWidths, onColumnWidthChange } =
    useSavedColumnWidths("routes");
  const [paginationModel, setPaginationModel] = usePaginationModel();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const {
    data: result,
    error: routeError,
    isLoading,
    isFetching,
  } = useListRoutesQuery(
    {
      organizationId,
      namespaceId: clusterId,
      includeDescendants: true,
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      filter: getFilter(filterModel),
      orderBy: getOrderBy(sortModel),
    },
    { skip: !organizationId || !clusterId },
  );
  const routes = result?.rows || [];
  const rowCount = result?.rowCount || 0;
  const estimatedRowCount = result?.estimatedRowCount || 0;

  const [deleteRoute] = useDeleteRouteMutation();

  async function handleMultiDelete() {
    await Promise.all(
      selected.map((id) =>
        deleteRoute({
          organizationId,
          routeId: id.toString(),
        }),
      ),
    );
  }

  const onCloseDeleteDialog = () => {
    setShowDelete(false);
    setSelected([]);
  };

  const deleteOne = () => {
    setSelected([routeId]);
    setShowDelete(true);
    actionsPopover.handleClose();
  };

  const FromCell = (route: Route) => {
    if (route.from?.startsWith("http") && !route.mcp) {
      return (
        <MuiLink target="_blank" href={route.from} noWrap>
          {route.from}
        </MuiLink>
      );
    }
    return (
      <Typography variant="inherit" noWrap>
        {route.from}
      </Typography>
    );
  };

  let columns: GridColDef[] = [
    {
      display: "flex",
      headerName: "Id",
      field: "id",
      flex: 1,
    },
    {
      display: "flex",
      headerName: "Originator",
      field: "originatorId",
      flex: 1,
    },
    {
      display: "flex",
      headerName: "Name",
      field: "name",
      flex: 1,
      renderCell: function NameCell(params: GridCellParams) {
        return (
          <Link to={`./${params.row.id}/edit`} noWrap>
            {params.row.name}
          </Link>
        );
      },
    },
    {
      display: "flex",
      headerName: "From",
      field: "from",
      flex: 3,
      renderCell: (params) => FromCell(params.row),
    },
    {
      display: "text",
      headerName: "Path",
      field: "path",
      flex: 1,
    },
    {
      display: "text",
      headerName: "Regex",
      field: "regex",
      flex: 1,
      valueGetter: (_value, row) => {
        const regex = row.regex;
        const priority = row.regexPriorityOrder;
        return regex ? regex + " (" + priority + ")" : "";
      },
      sortComparator: (v1, v2, p1, p2) => {
        const priority1 = p1.api.getCellValue(p1.id, "regexPriorityOrder");
        const priority2 = p2.api.getCellValue(p2.id, "regexPriorityOrder");
        if (priority1 === priority2) {
          return v1 > v2 ? -1 : 1;
        }
        return priority1 - priority2;
      },
    },
    {
      display: "text",
      headerName: "Prefix",
      field: "prefix",
      flex: 1,
    },
    {
      display: "flex",
      headerName: "To",
      field: "to",
      flex: 2,
      valueGetter: (_value, row) => {
        return row?.to?.join(",\n") || "";
      },
    },
    {
      display: "flex",
      headerName: "Assigned",
      field: "assigned",
      flex: 1,
      width: 150,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: function PolicyNameListCell(params) {
        const assigned = params.row.policyIds;
        if (!assigned.length) {
          return "None";
        }

        return (
          <Chip
            sx={{ m: 0.5 }}
            onClick={() =>
              navigate(
                "/app/management/routes/" +
                  params.row.id +
                  "/edit?tab=appliedPolicies",
              )
            }
            label={assigned?.length + " Policies"}
            size="small"
            component="span"
          />
        );
      },
    },
    {
      display: "flex",
      headerName: "Inherited",
      field: "inherited",
      flex: 1,
      width: 150,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: function EnforcedPolicyNameListCell(params) {
        const enforced = params.row.enforcedPolicyIds;
        if (!enforced.length) {
          return "None";
        }

        return (
          <Chip
            sx={{ m: 0.5 }}
            onClick={() =>
              navigate(
                "/app/management/routes/" +
                  params.row.id +
                  "/edit?tab=appliedPolicies",
              )
            }
            label={enforced?.length + " Policies"}
            size="small"
            component="span"
          />
        );
      },
    },
    updatedAt,
    {
      display: "flex",
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: function action(params) {
        return (
          <Grid
            container
            spacing={0}
            direction="column"
            alignItems="center"
            justifyContent="center"
          >
            <IconButton
              onClick={(e) => {
                anchorRef.current = e.currentTarget;
                setRouteId(params.row.id);
                actionsPopover.handleOpen();
              }}
              sx={{ width: 4 }}
            >
              <SvgIcon color="disabled">
                <EllipsisVerticalIcon />
              </SvgIcon>
            </IconButton>
          </Grid>
        );
      },
    },
  ];

  columns = mutateColumnWidths(columns);

  function ListViewCell(params: GridRenderCellParams) {
    return (
      <Stack sx={{ p: 0, m: 0 }}>
        <Stack direction="row" justifyContent="space-between">
          <Link to={`./${params.row.id}/edit`} noWrap>
            {params.row.name}
          </Link>
          <IconButton
            onClick={(e) => {
              anchorRef.current = e.currentTarget;
              setRouteId(params.row.id);
              actionsPopover.handleOpen();
            }}
            sx={{ width: 4, p: 0, m: 0 }}
          >
            <SvgIcon color="disabled">
              <EllipsisVerticalIcon />
            </SvgIcon>
          </IconButton>
        </Stack>
        {FromCell(params.row)}
      </Stack>
    );
  }

  const listColDef: GridListColDef = {
    field: "listColumn",
    renderCell: ListViewCell,
  };

  const CustomToolbar: FC = useCallback(
    () => (
      <Stack direction="row" justifyContent="space-between">
        {!isListView && (
          <DataGridProToolbar>
            <ActionToolbar
              selectedIds={selected}
              onDelete={() => setShowDelete(true)}
            />
          </DataGridProToolbar>
        )}
        <GridToolbarQuickFilter variant="outlined" size="small" sx={{ m: 1 }} />
      </Stack>
    ),
    [isListView, selected?.join("|"), setShowDelete],
  );

  if (routeError) {
    return (
      <Page title="Routes">
        <Header title="Routes" breadcrumbs={true} />
        <LoadingError />
      </Page>
    );
  }

  const actions = (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Quota quota="routes" label="routes" />
      {isConstrainedHostedCluster ? (
        <Button
          variant="default"
          disabled={organization?.readOnly || routeCount >= routeLimit}
          asChild
        >
          <RouterLink to="/app/management/routes/create">
            <PlusCircle />
            <span className="sr-only md:not-sr-only">New Route</span>
          </RouterLink>
        </Button>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Button
              variant="default"
              disabled={organization?.readOnly || routeCount >= routeLimit}
            >
              <PlusCircle />
              <span className="sr-only md:not-sr-only">New Route</span>
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent>
            <DropdownMenuItem asChild>
              <RouterLink to="/app/management/routes/create">
                Custom Route
              </RouterLink>
            </DropdownMenuItem>
            <DropdownMenuSeparator />

            <DropdownMenuItem disabled={!supportsSsh}>
              <RouterLink
                to="/app/management/routes/setup?type=ssh"
                className={cn(
                  "flex items-center gap-2",
                  !supportsSsh && "text-muted-foreground",
                )}
              >
                <Badge variant={supportsSsh ? "secondary" : "destructive"}>
                  {supportsSsh ? "Guided" : "Disabled"}
                </Badge>
                SSH Route
              </RouterLink>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <RouterLink
                to="/app/management/routes/kubernetes-setup"
                className="flex items-center gap-2"
              >
                <Badge variant="secondary">Guided</Badge>
                Kubernetes Route
              </RouterLink>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </Stack>
  );

  return (
    <Page title="Routes">
      <Header title="Routes" breadcrumbs={true} actions={actions} />
      <FeatureHint
        hintId="routes"
        title="What are Routes?"
        learnMoreLink="https://pomerium.com/docs/capabilities/routing"
      >
        <p>
          In Pomerium, <b>Routing</b> makes it possible for the Proxy service to
          manage requests and responses between a client and an upstream
          service.
        </p>
        <p>
          To route requests and responses to the correct locations, the Proxy
          service service requires two URLs:
        </p>
        <ul>
          <li>
            The <b>From</b> URL: this defines the external, public accessible
            URL of a proxied request. It tells the Proxy service to listen for
            requests from this location.
          </li>
          <li>
            The <b>To</b> URL: this defines the destination of the request, and
            can be an internal or external resource. It tells Pomerium where to
            proxy the request to.
          </li>
        </ul>
        <img
          src="/route-diagram.png"
          alt="Routes From-To Diagram"
          className="rounded-lg"
        />
        <Accordion
          type="single"
          collapsible
          onValueChange={(value: string) => {
            if (value) {
              sendStandardEvent("video_hint_opened", "routes", user);
            }
          }}
        >
          <AccordionItem value="routes_video">
            <AccordionTrigger>
              <span className="flex gap-2 items-center">
                <Video color="#06AED4" />
                Want to watch a video about Routes?
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <YouTube
                videoId="-wAKnj_cY-E"
                opts={{ height: "465", width: "825" }}
                title="Intro to Routes video"
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </FeatureHint>
      {showBanner && (
        <Alert
          severity="info"
          variant="outlined"
          sx={{
            mb: 1,
          }}
        >
          Try out these sample routes we created to get you started.
        </Alert>
      )}
      <DeleteActionDialog
        name="routes"
        open={showDelete}
        entries={
          routes?.filter((r) => selected.includes(r.id)) || ([] as Entry[])
        }
        onClose={onCloseDeleteDialog}
        onDelete={handleMultiDelete}
      />
      <Popover
        anchorEl={anchorRef.current}
        disableScrollLock
        onClose={actionsPopover.handleClose}
        open={actionsPopover.open}
        slotProps={{
          paper: {
            sx: {
              minWidth: 260,
            },
          },
        }}
      >
        <MenuList>
          <MenuItem
            onClick={() =>
              navigate("/app/management/routes/" + routeId + "/edit")
            }
          >
            Edit
          </MenuItem>
          <MenuItem onClick={deleteOne}>Delete</MenuItem>
        </MenuList>
      </Popover>
      <Container component={Paper} maxWidth={false} disableGutters={true}>
        <DataGridPro
          loading={isLoading || isFetching}
          getRowHeight={() => "auto"}
          rows={clusterId ? routes || [] : []}
          columns={columns}
          onRowSelectionModelChange={(model) => setSelected(model)}
          rowSelectionModel={selected}
          slots={{
            toolbar: CustomToolbar,
          }}
          columnVisibilityModel={JSON.parse(columnVisibilityModel)}
          onColumnVisibilityModelChange={(newModel) => {
            localStorage.setItem("routeColumns", JSON.stringify(newModel));
            setColumnVisibilityModel(JSON.stringify(newModel));
          }}
          onColumnWidthChange={onColumnWidthChange}
          unstable_listView={isListView}
          unstable_listColumn={listColDef}
          paginationMode="server"
          rowCount={rowCount}
          estimatedRowCount={estimatedRowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortingMode="server"
          onSortModelChange={setSortModel}
          onFilterModelChange={setFilterModel}
        />
      </Container>
    </Page>
  );
};
export default RouteList;
